import { useIntl } from "react-intl";
import IEmailTemplateSummaryDTO, { EmailTemplateGroup } from "../../../../models/DTO/IEmailTemplateSummaryDTO";
import OwBadge from "../../../shared/OwBadge/OwBadge";
import OwTranslate from "../../../shared/OwTranslate/OwTranslate";

type Props = {
    templates: IEmailTemplateSummaryDTO[];
    selectedKey: string | null;
    onSelect: (template: IEmailTemplateSummaryDTO) => void;
    disabled?: boolean;
};

const GROUPS: { group: EmailTemplateGroup; titleKey: string }[] = [
    { group: "workflow", titleKey: "workflow_steps" },
    { group: "order_event", titleKey: "order_events" },
    { group: "account", titleKey: "account_emails" },
];

/**
 * Left-hand list of every customizable email slot, grouped by kind, with badges that say
 * whether the supplier has customized it and whether that customization is active.
 */
const EmailTemplateList = (props: Props) => {
    const intl = useIntl();

    // Tooltip text: translated label plus the workflows that use the step. Workflow names are
    // supplier-entered except "Default", which is our own key.
    const workflowTitle = (t: IEmailTemplateSummaryDTO): string => {
        const label = intl.formatMessage({ id: t.labelKey, defaultMessage: t.label });
        const workflows = (t.workflows || []).map((w) => (w === "Default" ? intl.formatMessage({ id: "default" }) : w));

        return workflows.length ? `${label} — ${workflows.join(", ")}` : label;
    };

    return (
        <div className={"EmailTemplateList"}>
            {GROUPS.map(({ group, titleKey }) => {
                const rows = props.templates.filter((t) => t.group === group);

                if (!rows.length) {
                    return null;
                }

                return (
                    <div key={group}>
                        <div className={"group-title"}>{intl.formatMessage({ id: titleKey })}</div>
                        {rows.map((t) => (
                            <button
                                key={t.key}
                                type={"button"}
                                className={`template-row ${props.selectedKey === t.key ? "selected" : ""}`}
                                disabled={props.disabled}
                                onClick={() => props.onSelect(t)}
                                title={workflowTitle(t)}>
                                <span className={"row-label"}>
                                    <OwTranslate id={t.labelKey} defaultMessage={t.label} />
                                </span>
                                <span className={"row-flags"}>
                                    {t.group === "workflow" && t.sendsEmail === false ? (
                                        <OwBadge title={"not_sent"} bgColor={"secondary"} />
                                    ) : null}
                                    {t.customized && !t.is_active ? <OwBadge title={"inactive"} bgColor={"secondary"} /> : null}
                                    {t.customized && t.is_active && !t.isEditorDocument ? (
                                        <OwBadge title={"legacy"} bgColor={"warning"} textColor={"dark"} />
                                    ) : null}
                                    {t.customized && t.is_active && t.isEditorDocument ? (
                                        <OwBadge title={"custom"} bgColor={"success"} />
                                    ) : null}
                                </span>
                            </button>
                        ))}
                    </div>
                );
            })}
        </div>
    );
};

export default EmailTemplateList;
